import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import PageTitle from "../../components/DefaultStyles/PageTitle";

type JsonRecord = Record<string, unknown>;

interface EndpointDefinition {
  key: string;
  label: string;
  path: string;
  paginated: boolean;
  searchFields?: SearchFieldDefinition[];
  selectFilters?: SelectFilterDefinition[];
}

interface SearchFieldDefinition {
  key: string;
  label: string;
  placeholder: string;
}

interface SelectFilterDefinition {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
}

interface EndpointResult {
  data?: unknown;
  error?: string;
  loading: boolean;
  page: number;
}

type SearchValues = Record<string, Record<string, string>>;

interface PersistedViewState {
  activeKey: string;
  pages: Record<string, number>;
  searchValues: SearchValues;
  searchColumns: Record<string, string>;
}

const PAGE_SIZE = 20;
const VIEW_STATE_STORAGE_KEY = "youtube-personal-view-state";
const EMPTY_ENDPOINT_RESULT: EndpointResult = { loading: false, page: 1 };
const DATE_TIME_VALUE_PATTERN =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;
const TIME_ZONE_SUFFIX_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/;
const KST_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Seoul",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const YTP_GET_ENDPOINTS: EndpointDefinition[] = [
  { key: "tables", label: "테이블", path: "ytp", paginated: false },
  { key: "event", label: "event", path: "ytp/event/list", paginated: true },
  {
    key: "control",
    label: "control",
    path: "ytp/control/list",
    paginated: true,
  },
  {
    key: "schedule",
    label: "schedule",
    path: "ytp/schedule/list",
    paginated: true,
  },
  { key: "gap", label: "gap", path: "ytp/gap/list", paginated: true },
  { key: "link", label: "link", path: "ytp/link/list", paginated: true },
  {
    key: "membership",
    label: "membership",
    path: "ytp/membership/list",
    paginated: true,
  },
  {
    key: "account",
    label: "account",
    path: "ytp/account/list",
    paginated: true,
    searchFields: [
      { key: "email", label: "email", placeholder: "email 입력" },
      {
        key: "account_id",
        label: "account_id",
        placeholder: "account_id 입력",
      },
      {
        key: "nst_profile_key",
        label: "nst_profile_key",
        placeholder: "nst_profile_key 입력",
      },
    ],
    selectFilters: [
      {
        key: "execution_state",
        label: "execution_state",
        options: [
          { value: "idle", label: "idle - 현재 실행 작업 없음" },
          { value: "leased", label: "leased - 실행 작업 진행 중" },
          {
            value: "unknown_stop",
            label: "unknown_stop - 종료 여부 확인 중",
          },
          { value: "blocked", label: "blocked - 실행 차단" },
        ],
      },
      {
        key: "account_state",
        label: "account_state",
        options: [
          { value: "available", label: "available - 공급·할당 가능" },
          { value: "reserved", label: "reserved - 공급 작업 임시 선점" },
          { value: "allocated", label: "allocated - 고객/회차 할당" },
          { value: "investigating", label: "investigating - 조사 중" },
          { value: "retired", label: "retired - 사용 종료" },
        ],
      },
    ],
  },
  { key: "party", label: "party", path: "ytp/party/list", paginated: true },
  { key: "job", label: "job", path: "ytp/job/list", paginated: true },
  {
    key: "access",
    label: "access",
    path: "ytp/access/list",
    paginated: true,
  },
  { key: "pc", label: "pc", path: "ytp/pc/list", paginated: true },
  {
    key: "round",
    label: "round",
    path: "ytp/round/list",
    paginated: true,
    searchFields: [
      { key: "party_id", label: "party_id", placeholder: "party_id 입력" },
    ],
  },
];

const createInitialResults = (
  pages: Record<string, number> = {},
): Record<string, EndpointResult> =>
  YTP_GET_ENDPOINTS.reduce<Record<string, EndpointResult>>((results, endpoint) => {
    results[endpoint.key] = {
      loading: false,
      page: pages[endpoint.key] ?? 1,
    };
    return results;
  }, {});

const createInitialSearchValues = (): SearchValues =>
  YTP_GET_ENDPOINTS.reduce<SearchValues>((values, endpoint) => {
    values[endpoint.key] = {};
    (endpoint.searchFields ?? []).forEach((field) => {
      values[endpoint.key][field.key] = "";
    });
    (endpoint.selectFilters ?? []).forEach((filter) => {
      values[endpoint.key][filter.key] = "";
    });
    return values;
  }, {});

const createInitialSearchColumns = (): Record<string, string> =>
  YTP_GET_ENDPOINTS.reduce<Record<string, string>>((columns, endpoint) => {
    columns[endpoint.key] = endpoint.searchFields?.[0]?.key ?? "";
    return columns;
  }, {});

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const loadPersistedViewState = (): PersistedViewState => {
  const defaultSearchValues = createInitialSearchValues();
  const defaultSearchColumns = createInitialSearchColumns();
  const defaultPages = YTP_GET_ENDPOINTS.reduce<Record<string, number>>(
    (pages, endpoint) => {
      pages[endpoint.key] = 1;
      return pages;
    },
    {},
  );
  const defaultState: PersistedViewState = {
    activeKey: YTP_GET_ENDPOINTS[0].key,
    pages: defaultPages,
    searchValues: defaultSearchValues,
    searchColumns: defaultSearchColumns,
  };

  try {
    const savedValue = window.sessionStorage.getItem(VIEW_STATE_STORAGE_KEY);
    if (!savedValue) return defaultState;

    const parsedValue: unknown = JSON.parse(savedValue);
    if (!isRecord(parsedValue)) return defaultState;

    const activeKey =
      typeof parsedValue.activeKey === "string" &&
      YTP_GET_ENDPOINTS.some((endpoint) => endpoint.key === parsedValue.activeKey)
        ? parsedValue.activeKey
        : defaultState.activeKey;
    const savedPages = isRecord(parsedValue.pages) ? parsedValue.pages : {};
    const savedSearchValues = isRecord(parsedValue.searchValues)
      ? parsedValue.searchValues
      : {};
    const savedSearchColumns = isRecord(parsedValue.searchColumns)
      ? parsedValue.searchColumns
      : {};

    YTP_GET_ENDPOINTS.forEach((endpoint) => {
      const savedPage = savedPages[endpoint.key];
      if (typeof savedPage === "number" && savedPage >= 1) {
        defaultPages[endpoint.key] = savedPage;
      }

      const endpointSearchValues = savedSearchValues[endpoint.key];
      if (isRecord(endpointSearchValues)) {
        [
          ...(endpoint.searchFields ?? []),
          ...(endpoint.selectFilters ?? []),
        ].forEach((field) => {
          const savedSearchValue = endpointSearchValues[field.key];
          if (typeof savedSearchValue === "string") {
            defaultSearchValues[endpoint.key][field.key] = savedSearchValue;
          }
        });
      }

      const savedSearchColumn = savedSearchColumns[endpoint.key];
      if (
        typeof savedSearchColumn === "string" &&
        (endpoint.searchFields ?? []).some((field) => field.key === savedSearchColumn)
      ) {
        defaultSearchColumns[endpoint.key] = savedSearchColumn;
      }
    });

    return {
      activeKey,
      pages: defaultPages,
      searchValues: defaultSearchValues,
      searchColumns: defaultSearchColumns,
    };
  } catch {
    return defaultState;
  }
};

const findRows = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;
  if (!isRecord(data)) return [];

  const rowKeys = ["items", "rows", "list", "results"];
  for (const key of rowKeys) {
    if (Array.isArray(data[key])) return data[key] as unknown[];
  }

  if (Array.isArray(data.data)) return data.data;
  if (isRecord(data.data)) {
    const nestedRows = findRows(data.data);
    if (nestedRows.length > 0) return nestedRows;
  }

  return [data];
};

const findNumber = (data: unknown, keys: string[]): number | undefined => {
  if (!isRecord(data)) return undefined;

  for (const key of keys) {
    if (typeof data[key] === "number") return data[key] as number;
  }

  for (const nestedKey of ["meta", "pagination", "data"]) {
    const nestedValue = findNumber(data[nestedKey], keys);
    if (nestedValue !== undefined) return nestedValue;
  }

  return undefined;
};

const getColumns = (rows: unknown[], excludedColumns: string[] = []): string[] => {
  const excludedColumnSet = new Set(excludedColumns);
  const columns = new Set<string>();
  rows.forEach((row) => {
    if (isRecord(row)) {
      Object.keys(row).forEach((key) => {
        if (!excludedColumnSet.has(key)) columns.add(key);
      });
    }
  });
  return Array.from(columns);
};

const buildSearchParams = (values: Record<string, string>) =>
  Object.entries(values).reduce<Record<string, string>>((params, [key, value]) => {
    const trimmedValue = value.trim();
    if (trimmedValue !== "") params[key] = trimmedValue;
    return params;
  }, {});

const isTimeColumn = (column?: string) =>
  column !== undefined && /(?:_at|_date|_time|At|Date|Time)$/i.test(column);

const formatKstDateTime = (value: string) => {
  const trimmedValue = value.trim();
  if (!DATE_TIME_VALUE_PATTERN.test(trimmedValue)) return value;

  const isoValue = trimmedValue.replace(" ", "T");
  const zonedValue = TIME_ZONE_SUFFIX_PATTERN.test(isoValue)
    ? isoValue
    : `${isoValue}+09:00`;
  const date = new Date(zonedValue);
  if (Number.isNaN(date.getTime())) return value;

  const parts = KST_DATE_TIME_FORMATTER.formatToParts(date).reduce<
    Record<string, string>
  >((result, part) => {
    if (part.type !== "literal") result[part.type] = part.value;
    return result;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

const renderCellValue = (value: unknown, column?: string) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "string" && isTimeColumn(column)) {
    return formatKstDateTime(value);
  }
  return String(value);
};

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (isRecord(responseData) && typeof responseData.message === "string") {
      return responseData.message;
    }
    return error.message;
  }
  return "데이터를 불러오지 못했습니다.";
};

export default function YoutubePersonal() {
  const navigate = useNavigate();
  const lastOpenedKey = useRef<string>();
  const [initialViewState] = useState(loadPersistedViewState);
  const [activeKey, setActiveKey] = useState(initialViewState.activeKey);
  const [results, setResults] = useState<Record<string, EndpointResult>>(
    () => createInitialResults(initialViewState.pages),
  );
  const [searchValues, setSearchValues] = useState<SearchValues>(
    initialViewState.searchValues,
  );
  const [searchColumns, setSearchColumns] = useState<Record<string, string>>(
    initialViewState.searchColumns,
  );
  const searchValuesRef = useRef(searchValues);
  searchValuesRef.current = searchValues;
  const resultsRef = useRef(results);
  resultsRef.current = results;

  const fetchEndpoint = useCallback(async (
    endpoint: EndpointDefinition,
    page = 1,
    searchParams: Record<string, string> = {},
  ) => {
    setResults((current) => ({
      ...current,
      [endpoint.key]: {
        ...(current[endpoint.key] ?? EMPTY_ENDPOINT_RESULT),
        loading: true,
        error: undefined,
        page,
      },
    }));

    try {
      const response = await axios.get(`/api/${endpoint.path}`, {
        params: endpoint.paginated
          ? { page, limit: PAGE_SIZE, ...buildSearchParams(searchParams) }
          : undefined,
      });

      setResults((current) => ({
        ...current,
        [endpoint.key]: { data: response?.data, loading: false, page },
      }));
    } catch (error) {
      setResults((current) => ({
        ...current,
        [endpoint.key]: {
          ...(current[endpoint.key] ?? EMPTY_ENDPOINT_RESULT),
          error: getErrorMessage(error),
          loading: false,
          page,
        },
      }));
    }
  }, []);

  const activeEndpoint =
    YTP_GET_ENDPOINTS.find((endpoint) => endpoint.key === activeKey) ??
    YTP_GET_ENDPOINTS[0];

  useEffect(() => {
    if (lastOpenedKey.current === activeKey) return;
    lastOpenedKey.current = activeKey;
    const savedPage = resultsRef.current[activeKey]?.page ?? 1;
    void fetchEndpoint(
      activeEndpoint,
      savedPage,
      searchValuesRef.current[activeKey],
    );
  }, [activeEndpoint, activeKey, fetchEndpoint]);

  useEffect(() => {
    const pages = YTP_GET_ENDPOINTS.reduce<Record<string, number>>(
      (savedPages, endpoint) => {
        savedPages[endpoint.key] = results[endpoint.key]?.page ?? 1;
        return savedPages;
      },
      {},
    );

    try {
      window.sessionStorage.setItem(
        VIEW_STATE_STORAGE_KEY,
        JSON.stringify({ activeKey, pages, searchValues, searchColumns }),
      );
    } catch {
      // 저장소 접근이 제한된 환경에서는 현재 화면 상태만 유지합니다.
    }
  }, [activeKey, results, searchColumns, searchValues]);

  const activeResult = results[activeEndpoint.key] ?? EMPTY_ENDPOINT_RESULT;
  const rows = useMemo(() => findRows(activeResult?.data), [activeResult?.data]);
  const columns = useMemo(
    () => getColumns(rows, activeKey === "account" ? ["owner_pc_id"] : []),
    [activeKey, rows],
  );
  const activeSearchValues = searchValues[activeKey] ?? {};
  const activeSearchColumn =
    searchColumns[activeKey] ?? activeEndpoint.searchFields?.[0]?.key ?? "";
  const total = findNumber(activeResult?.data, ["total", "totalCount", "count"]);
  const totalPages = total === undefined ? undefined : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPreviousPage = activeResult.page > 1;
  const hasNextPage =
    totalPages !== undefined
      ? activeResult.page < totalPages
      : rows.length === PAGE_SIZE;

  const handleSearchValueChange = (fieldKey: string, value: string) => {
    setSearchValues((current) => ({
      ...current,
      [activeKey]: (activeEndpoint.searchFields ?? []).reduce<
        Record<string, string>
      >(
        (fields, field) => {
          fields[field.key] = field.key === fieldKey ? value : "";
          return fields;
        },
        { ...(current[activeKey] ?? {}) },
      ),
    }));
  };

  const handleSearchColumnChange = (fieldKey: string) => {
    setSearchColumns((current) => ({ ...current, [activeKey]: fieldKey }));
    setSearchValues((current) => ({
      ...current,
      [activeKey]: (activeEndpoint.searchFields ?? []).reduce<
        Record<string, string>
      >(
        (fields, field) => {
          fields[field.key] = "";
          return fields;
        },
        { ...(current[activeKey] ?? {}) },
      ),
    }));
  };

  const handleSelectFilterChange = (filterKey: string, value: string) => {
    setSearchValues((current) => ({
      ...current,
      [activeKey]: {
        ...(current[activeKey] ?? {}),
        [filterKey]: value,
      },
    }));
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void fetchEndpoint(activeEndpoint, 1, activeSearchValues);
  };

  const handleResetSearch = () => {
    const emptyValues = createInitialSearchValues()[activeKey] ?? {};
    setSearchValues((current) => ({ ...current, [activeKey]: emptyValues }));
    void fetchEndpoint(activeEndpoint, 1);
  };

  const handleAccountClick = (accountId: unknown) => {
    if (accountId === null || accountId === undefined || accountId === "") return;

    const value = String(accountId);
    const accountEndpoint = YTP_GET_ENDPOINTS.find(
      (endpoint) => endpoint.key === "account",
    );
    if (!accountEndpoint) return;

    const nextAccountSearch = {
      ...createInitialSearchValues().account,
      account_id: value,
    };
    setSearchValues((current) => ({
      ...current,
      account: nextAccountSearch,
    }));
    setSearchColumns((current) => ({ ...current, account: "account_id" }));
    lastOpenedKey.current = "account";
    setActiveKey("account");
    void fetchEndpoint(accountEndpoint, 1, nextAccountSearch);
  };

  return (
    <>
      <PageTitle title="유튜브 개인" />

      <Description>
        YTP 통합 관리 GET API를 탭별로 조회합니다. 선택한 탭의 데이터만 불러옵니다.
      </Description>

      <TabList role="tablist" aria-label="YTP 통합 관리 조회 탭">
        {YTP_GET_ENDPOINTS.map((endpoint) => {
          const result = results[endpoint.key] ?? EMPTY_ENDPOINT_RESULT;
          return (
            <TabButton
              key={endpoint.key}
              type="button"
              role="tab"
              aria-selected={activeKey === endpoint.key}
              isActive={activeKey === endpoint.key}
              onClick={() => setActiveKey(endpoint.key)}
            >
              {endpoint.label}
              {result.loading ? <StatusDot aria-label="조회 중" /> : null}
              {result.error ? <ErrorMark aria-label="조회 실패">!</ErrorMark> : null}
            </TabButton>
          );
        })}
      </TabList>

      {(activeEndpoint.searchFields?.length ?? 0) +
        (activeEndpoint.selectFilters?.length ?? 0) >
      0 ? (
        <SearchForm onSubmit={handleSearch}>
          {(activeEndpoint.searchFields?.length ?? 0) > 1 ? (
            <>
              <SearchField>
                <SearchLabel htmlFor={`ytp-${activeKey}-column`}>
                  검색 컬럼
                </SearchLabel>
                <SearchSelect
                  id={`ytp-${activeKey}-column`}
                  value={activeSearchColumn}
                  onChange={(event) => handleSearchColumnChange(event.target.value)}
                >
                  {activeEndpoint.searchFields?.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}
                    </option>
                  ))}
                </SearchSelect>
              </SearchField>
              <SearchField>
                <SearchLabel htmlFor={`ytp-${activeKey}-value`}>
                  검색값
                </SearchLabel>
                <SearchInput
                  id={`ytp-${activeKey}-value`}
                  value={activeSearchValues[activeSearchColumn] ?? ""}
                  placeholder={`${activeSearchColumn} 입력`}
                  onChange={(event) =>
                    handleSearchValueChange(activeSearchColumn, event.target.value)
                  }
                />
              </SearchField>
            </>
          ) : (
            activeEndpoint.searchFields?.map((field) => (
              <SearchField key={field.key}>
                <SearchLabel htmlFor={`ytp-${activeKey}-${field.key}`}>
                  {field.label}
                </SearchLabel>
                <SearchInput
                  id={`ytp-${activeKey}-${field.key}`}
                  value={activeSearchValues[field.key] ?? ""}
                  placeholder={field.placeholder}
                  onChange={(event) =>
                    handleSearchValueChange(field.key, event.target.value)
                  }
                />
              </SearchField>
            ))
          )}
          {activeEndpoint.selectFilters?.map((filter) => (
            <SearchField key={filter.key}>
              <SearchLabel htmlFor={`ytp-${activeKey}-${filter.key}`}>
                {filter.label}
              </SearchLabel>
              <SearchSelect
                id={`ytp-${activeKey}-${filter.key}`}
                value={activeSearchValues[filter.key] ?? ""}
                onChange={(event) =>
                  handleSelectFilterChange(filter.key, event.target.value)
                }
              >
                <option value="">전체</option>
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SearchSelect>
            </SearchField>
          ))}
          <SearchButton type="submit">검색</SearchButton>
          <ResetButton type="button" onClick={handleResetSearch}>
            초기화
          </ResetButton>
        </SearchForm>
      ) : null}

      <ContentCard role="tabpanel">
        <ContentHeader>
          <div>
            <ContentTitle>{activeEndpoint.label}</ContentTitle>
            <EndpointPath>GET /api/{activeEndpoint.path}</EndpointPath>
          </div>
          <RefreshButton
            type="button"
            onClick={() =>
              void fetchEndpoint(activeEndpoint, activeResult.page, activeSearchValues)
            }
          >
            현재 탭 새로고침
          </RefreshButton>
        </ContentHeader>

        {activeResult.loading ? (
          <StateMessage>조회 중입니다.</StateMessage>
        ) : activeResult.error ? (
          <ErrorState>
            <strong>조회에 실패했습니다.</strong>
            <span>{activeResult.error}</span>
          </ErrorState>
        ) : rows.length === 0 ? (
          <StateMessage>조회된 데이터가 없습니다.</StateMessage>
        ) : columns.length === 0 ? (
          <RawValue>{rows.map((row) => renderCellValue(row)).join(", ")}</RawValue>
        ) : (
          <TableScroll>
            <DataTable>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((column) => (
                      <td
                        key={column}
                        title={renderCellValue(
                          isRecord(row) ? row[column] : row,
                          column,
                        )}
                      >
                        {column === "party_id" && activeKey === "round" ? (
                          <CellLink
                            type="button"
                            onClick={() =>
                              navigate(`/party/info/${renderCellValue(isRecord(row) ? row[column] : row)}`)
                            }
                          >
                            {renderCellValue(isRecord(row) ? row[column] : row)}
                          </CellLink>
                        ) : column === "account_id" ? (
                          <CellLink
                            type="button"
                            onClick={() =>
                              handleAccountClick(isRecord(row) ? row[column] : row)
                            }
                          >
                            {renderCellValue(isRecord(row) ? row[column] : row)}
                          </CellLink>
                        ) : (
                          renderCellValue(
                            isRecord(row) ? row[column] : row,
                            column,
                          )
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableScroll>
        )}

        {activeEndpoint.paginated && !activeResult.loading && !activeResult.error ? (
          <PaginationRow>
            <PageButton
              type="button"
              disabled={!hasPreviousPage}
              onClick={() =>
                void fetchEndpoint(
                  activeEndpoint,
                  activeResult.page - 1,
                  activeSearchValues,
                )
              }
            >
              이전
            </PageButton>
            <PageInfo>
              {activeResult.page} 페이지
              {total !== undefined ? ` · 총 ${total.toLocaleString()}건` : ""}
            </PageInfo>
            <PageButton
              type="button"
              disabled={!hasNextPage}
              onClick={() =>
                void fetchEndpoint(
                  activeEndpoint,
                  activeResult.page + 1,
                  activeSearchValues,
                )
              }
            >
              다음
            </PageButton>
          </PaginationRow>
        ) : null}
      </ContentCard>
    </>
  );
}

const Description = styled.p`
  margin: -24px 0 20px;
  color: #606060;
  font-size: 14px;
  line-height: 21px;
`;

const RefreshButton = styled.button`
  min-height: 36px;
  padding: 7px 14px;
  border: 1px solid #c2cad4;
  border-radius: 5px;
  background: #ffffff;
  color: #505050;
  font-size: 14px;
  font-weight: 600;

  &:hover {
    border-color: #00c7c7;
    color: #009a9a;
  }
`;

const TabList = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding-bottom: 1px;
  border-bottom: 1px solid #d4d9e1;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  position: relative;
  flex: 0 0 auto;
  min-width: 76px;
  padding: 12px 14px;
  border-bottom: 3px solid
    ${({ isActive }) => (isActive ? "#00beb0" : "transparent")};
  color: ${({ isActive }) => (isActive ? "#2f2f38" : "#858c97")};
  font-size: 14px;
  font-weight: ${({ isActive }) => (isActive ? 700 : 500)};
`;

const SearchForm = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding: 20px 24px;
  border: 1px solid #d4d9e1;
  border-radius: 5px;
  background: #ffffff;
  flex-wrap: wrap;
`;

const SearchField = styled.div`
  display: flex;
  min-width: 200px;
  flex: 1;
  flex-direction: column;
  gap: 6px;
`;

const SearchLabel = styled.label`
  color: #606060;
  font-size: 13px;
  font-weight: 600;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d4d9e1;
  border-radius: 5px;
  color: #2f2f38;
  font-size: 14px;

  &:focus {
    border-color: #00beb0;
  }
`;

const SearchSelect = styled.select`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d4d9e1;
  border-radius: 5px;
  background: #ffffff;
  color: #2f2f38;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #00beb0;
  }
`;

const SearchButton = styled.button`
  height: 38px;
  padding: 0 20px;
  border-radius: 5px;
  background: #00c7c7;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;

  &:hover {
    background: #0ddddd;
  }
`;

const ResetButton = styled(SearchButton)`
  border: 1px solid #c2cad4;
  background: #ffffff;
  color: #606060;

  &:hover {
    border-color: #858c97;
    background: #f5f5f5;
  }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background: #00beb0;
`;

const ErrorMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  margin-left: 6px;
  border-radius: 50%;
  background: #f42d16;
  color: #ffffff;
  font-size: 10px;
`;

const ContentCard = styled.section`
  margin-top: 20px;
  padding: 24px;
  border: 1px solid #d4d9e1;
  border-radius: 5px;
  background: #ffffff;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
`;

const ContentTitle = styled.h2`
  color: #2f2f38;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
`;

const EndpointPath = styled.code`
  color: #808080;
  font-size: 12px;
`;

const StateMessage = styled.div`
  display: flex;
  min-height: 240px;
  align-items: center;
  justify-content: center;
  color: #808080;
  font-size: 15px;
`;

const ErrorState = styled(StateMessage)`
  flex-direction: column;
  gap: 8px;
  color: #f42d16;

  span {
    color: #808080;
    font-size: 13px;
  }
`;

const RawValue = styled.pre`
  min-height: 100px;
  overflow: auto;
  padding: 16px;
  border-radius: 5px;
  background: #f6f6f8;
  white-space: pre-wrap;
`;

const TableScroll = styled.div`
  max-width: 100%;
  overflow-x: auto;
`;

const DataTable = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 10px;
    border-bottom: 1px solid #d4d9e1;
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
  }

  th {
    color: #858c97;
    font-size: 14px;
    font-weight: 700;
  }

  td {
    max-width: 360px;
    overflow: hidden;
    color: #2f2f38;
    font-size: 14px;
    text-overflow: ellipsis;
  }
`;

const CellLink = styled.button`
  padding: 0;
  color: #009a9a;
  font: inherit;
  text-decoration: underline;

  &:hover {
    color: #00c7c7;
  }
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 24px;
`;

const PageButton = styled(RefreshButton)`
  min-width: 64px;

  &:disabled {
    border-color: #ebebeb;
    color: #aaaaaa;
  }
`;

const PageInfo = styled.span`
  min-width: 120px;
  color: #606060;
  font-size: 14px;
  text-align: center;
`;
